import React, { useEffect, useCallback, useMemo, useState } from "react";
import { Head, usePage } from "@inertiajs/react";

import LocationSelector from "@/components/location/LocationSelector";
import CarouselCategoryTabs from "@/components/ui/CarouselCategoryTabs";
import SmartLink from "@/components/ui/SmartLink";
import LazyImageArticleCard from "@/components/articles/LazyImageArticleCard";

import { useLocationObserver } from "@/hooks/useLocationObserver";

import HomeHeader from "@/components/layouts/headers/HomeHeader";
import HomeFooter from "@/components/layouts/footers/HomeFooter";

import AdsBlock from "@/components/ads/AdsBlock";
import { AdItem, AdOrientation } from "@/components/ads/AdSlot";
import { News, Category, Location, Tag } from "@/types";

import { ScrollToTop } from "@/components/ui/ScrollToTop";
import LaporLambeButton from "@/components/ui/LaporLambeButton";

import DynamicNewsSection from "@/components/sections/DynamicNewsSection";
import { useHomepageLocationFilter } from "@/hooks/useUnifiedLocationFilter";
import {
    useLatestNews,
    usePopularNews,
    useMostSeeNews,
    useCelebrityNews,
} from "@/hooks/useOptimizedNews";

import type { HomepageProps } from "@/types/pages/homepage";

const Homepage: React.FC<HomepageProps> = ({
    user,
    pageTitle = "Lambe Turah News",
    latestNews: initialLatestNews = [],
    hotNews: initialHotNews = [],
    popularNews: initialPopularNews = [],
    mostViewedThisMonth: initialMostViewedThisMonth = [],
    celebrityNews: initialCelebrityNews = [],
    categories = [],
    locations = [],
    tags = [],
}) => {
    const { url } = usePage();

    // Homepage location filter with full URL sync and auto-refresh
    const locationFilter = useHomepageLocationFilter();

    const [showScrollToTop, setShowScrollToTop] = useState(false);

    // Use observer-based location loading instead of immediate fetch
    const {
        locations: apiLocations,
        isLoading: isLoadingLocations,
        error: locationError,
        targetRef: locationTargetRef,
    } = useLocationObserver({
        fallbackLocations: locations || [],
        enabled: !locations || locations.length === 0,
    });

    // Handle scroll to show/hide scroll-to-top button
    useEffect(() => {
        const handleScroll = () => {
            try {
                const scrollTop =
                    window.pageYOffset || document.documentElement.scrollTop;
                setShowScrollToTop(scrollTop > 300);
            } catch (error) {
                console.log(error);
            }
        };

        // Add scroll event listener
        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // Fetch news data using optimized hooks
    const { data: latestNews = initialLatestNews } = useLatestNews({
        locationSlug: locationFilter.currentLocationSlug,
        limit: 8,
    });

    const { data: popularNews = initialPopularNews } = usePopularNews({
        locationSlug: locationFilter.currentLocationSlug,
        limit: 10,
    });

    const { data: mostSeeNews = initialMostViewedThisMonth } = useMostSeeNews({
        locationSlug: locationFilter.currentLocationSlug,
        limit: 8,
    });

    const { data: celebrityNews = initialCelebrityNews } = useCelebrityNews({
        locationSlug: locationFilter.currentLocationSlug,
        limit: 4,
    });

    // Handle location change
    const handleLocationChange = useCallback(
        (location: Location | null) => {
            locationFilter.setLocation(location);
        },
        [locationFilter],
    );

    // Create category tabs array with "Beranda" tab first, optimized with useMemo
    const categoryTabs = useMemo(() => {
        const filteredCategories = categories
            .filter(
                (cat) =>
                    cat.name.toLowerCase() !== "hot news" &&
                    cat.slug !== "hot-news",
            )
            .sort((a, b) => {
                // Sort by order field first, then by name as fallback
                const orderA = a.order ?? 999;
                const orderB = b.order ?? 999;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.name.localeCompare(b.name);
            });

        return [
            { id: "home", label: "Beranda", color: "red", slug: undefined },
            ...filteredCategories.map((cat: Category) => ({
                id: cat.id,
                label: cat.name,
                color: "red",
                icon: undefined,
                slug: cat.slug,
                order: cat.order,
            })),
        ];
    }, [categories]);

    // Determine active tab based on current URL using Inertia's usePage hook
    const activeTab = useMemo(() => {
        const currentPath = url || "/";

        if (currentPath === "/") {
            return "home";
        } else if (currentPath.startsWith("/category/")) {
            const activeSlug = currentPath.replace("/category/", "");
            return activeSlug;
        } else {
            return categories[0]?.id || "home";
        }
    }, [url, categories]);

    return (
        <>
            <Head title={pageTitle} />
            <HomeHeader user={user} categories={categories} />

            {/* Banner Ad */}
            <section className="container mx-auto  px-2 md:px-4 my-2 md:my-12">
                {(() => {
                    const heroAds = [
                        {
                            id: "hero-1",
                            filename: "h_banner_entertainment.jpg",
                            href: "",
                            alt: "Ad Banner Entertainment",
                            priority: true,
                            orientation: "horizontal" as AdOrientation,
                            aspectRatio: "6/1",
                        },
                    ];
                    return <AdsBlock items={heroAds} />;
                })()}
            </section>

            <section className="container mx-auto mt-4 md:mt-0 md:px-4 lg:px-8 ">
                {/* Location selector */}
                <div ref={locationTargetRef as React.RefObject<HTMLDivElement>}>
                    <LocationSelector
                        locations={
                            apiLocations.length > 0 ? apiLocations : locations
                        }
                        selectedLocation={locationFilter.currentLocation}
                        onChange={handleLocationChange}
                    />
                </div>
            </section>

            <section className="sticky top-32 md:top-22 z-30 bg-lg-background/30 dark:bg-dr-background/60 backdrop-blur-lg w-full">
                <div className="mx-auto container px-2 md:px-4 md:py-1 flex items-center justify-between gap-2 md:gap-4">
                    {/* Category Tabs - scrollable */}
                    <CarouselCategoryTabs
                        activeTab={activeTab}
                        showNavigationButtons={false}
                        autoSizeWidth={true}
                        showNewsCount={false}
                    />
                </div>
            </section>

            {/* Main Content */}
            <main className="container mx-auto lg:px-8 mt-6 md:mt-20">
                {/* main articles */}
                <section className="flex flex-col lg:flex-row items-start md:items-stretch lg:gap-4">
                    <div className="w-full flex flex-col lg:flex-3/4 gap-16 md:pr-8 lg:border-r border-r-gray-200 dark:border-gray-700">
                        <h2 className="text-4xl md:text-4xl font-bold text-gray-900 dark:text-white -mb-8 mx-2 md:mx-0">
                            Berita Terbaru
                        </h2>

                        {/* Top 2 Cards Layout */}
                        {(latestNews?.length || 0) > 0 && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-2 lg:mx-0">
                                    {latestNews
                                        .slice(0, 2)
                                        .map((newsItem: News) => (
                                            <LazyImageArticleCard
                                                key={newsItem.id}
                                                news={newsItem}
                                                variant="hero"
                                            />
                                        ))}
                                </div>

                                {/* List Layout for More News */}
                                <div className="px-2">
                                    {latestNews
                                        .slice(2, 8)
                                        .map((newsItem: News) => (
                                            <LazyImageArticleCard
                                                key={newsItem.id}
                                                news={newsItem}
                                                variant="compact"
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                        {/* Ads */}

                        {(() => {
                            const heroAds = [
                                {
                                    id: "hero-2",
                                    filename: "h_banner_entertainment.jpg",
                                    href: "https://www.instagram.com/lugu.entertainment/?hl=en",
                                    alt: "Lugu Entertainment Banner",
                                    orientation: "horizontal" as AdOrientation,
                                    aspectRatio: "6/1",
                                    priority: true,
                                },
                            ];
                            return (
                                <AdsBlock items={heroAds} className="mx-2" />
                            );
                        })()}
                    </div>

                    {/* Right Sidebar */}
                    <div className="w-full lg:flex-1/4 mt-8 md:mt-0 space-y-4 md:pt-6">
                        {/* Ssst... Ini Juga Seru! (Celebrity) - Using DynamicNewsSection */}
                        <div className="bg-white dark:bg-gray-800/40 md:rounded-lg overflow-hidden">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-3 my-4 px-4">
                                <span className="w-fit bg-[#de0b66] text-white text-xs px-3 py-1 rounded-full font-semibold">
                                    Selebriti
                                </span>
                                <h3 className="lg:oder-first text-2xl lg:text-base font-bold text-gray-900 dark:text-white">
                                    Ssst... Ini Juga Seru!
                                </h3>
                            </div>
                            <div className="pl-6 md:pl-4 pr-2 lg:pr-4 pb-4">
                                <DynamicNewsSection
                                    type="celebrity"
                                    categorySlug="selebriti"
                                    locationSlug={
                                        locationFilter.currentLocationSlug
                                    }
                                    sortBy="latest"
                                    variant="list"
                                    autoLoadOnScroll={false}
                                    showLoadMoreButton={false}
                                    showViewAll={false}
                                    maxItems={4}
                                    pageSize={8}
                                    className=""
                                />
                            </div>
                        </div>

                        {(() => {
                            const heroAds: AdItem[] = [
                                {
                                    id: "hero-2",
                                    filename: "v_banner_entertainment.jpg",
                                    href: "https://www.instagram.com/lugu.production/?hl=en",
                                    orientation: "vertical",
                                    aspectRatio: "4/5",
                                    alt: "Lugu Production Ads",
                                },
                            ];
                            return (
                                <AdsBlock
                                    items={heroAds}
                                    className="md:rounded-lg mx-2 md:mx-0"
                                />
                            );
                        })()}
                    </div>
                </section>

                <section className="flex flex-col lg:flex-row items-start md:items-stretch gap-6 lg:gap-4 ">
                    <div className="flex flex-col md:my-16 lg:my-0 lg:flex-3/4 gap-16 md:pt-16 lg:pr-8 lg:border-r border-r-gray-200 dark:border-gray-700">
                        {(mostSeeNews?.length || 0) > 0 && (
                            <>
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4 -mb-8 mx-2">
                                    <span className="w-fit bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                                        Most View
                                    </span>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Paling Banyak Dilihat
                                    </h2>
                                </div>
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6  mx-2 lg:mx-0">
                                        {mostSeeNews
                                            .slice(0, 2)
                                            .map((newsItem: News) => (
                                                <LazyImageArticleCard
                                                    key={newsItem.id}
                                                    news={newsItem}
                                                    variant="hero"
                                                />
                                            ))}
                                    </div>

                                    <div className="mx-2">
                                        {mostSeeNews
                                            .slice(2, 8)
                                            .map((newsItem: News) => (
                                                <LazyImageArticleCard
                                                    key={newsItem.id}
                                                    news={newsItem}
                                                    variant="compact"
                                                />
                                            ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Viral Section with DynamicNewsSection */}
                        <DynamicNewsSection
                            title="VIRAL 🔥"
                            type="viral"
                            categorySlug="viral"
                            locationSlug={locationFilter.currentLocationSlug}
                            sortBy="views"
                            layout="double"
                            cardVariant="compact"
                            autoLoadOnScroll={false}
                            showLoadMoreButton={true}
                            maxItems={16}
                            pageSize={16}
                            badge={{ text: "Trending", color: "bg-orange-500" }}
                            className=" mx-2 lg:mx-0"
                        />

                        {(() => {
                            const heroAds: AdItem[] = [
                                {
                                    id: "hero-1",
                                    filename: "1440x240_Lugu-Software.jpg",
                                    href: "https://software.lugu.id",
                                    orientation: "horizontal",
                                    aspectRatio: "6/1",
                                    alt: "Lugu Software Ads",
                                },
                            ];
                            return (
                                <AdsBlock
                                    items={heroAds}
                                    className="md:rounded-lg"
                                />
                            );
                        })()}
                        {/* Celebrity Section with DynamicNewsSection */}
                        <DynamicNewsSection
                            title="Selebriti"
                            type="celebrity"
                            categorySlug="selebriti"
                            locationSlug={locationFilter.currentLocationSlug}
                            sortBy="latest"
                            layout="double"
                            cardVariant="compact"
                            autoLoadOnScroll={false}
                            showLoadMoreButton={true}
                            maxItems={16}
                            pageSize={16}
                            badge={{ text: "Hot", color: "bg-purple-500" }}
                            className=" mx-2 lg:mx-0"
                        />

                        {/* Dynamic Category Sections */}
                        {categories.slice(0, 3).map((category) => (
                            <DynamicNewsSection
                                key={`category-${category.slug}`}
                                title={category.name}
                                categorySlug={category.slug}
                                locationSlug={
                                    locationFilter.currentLocationSlug
                                }
                                sortBy="latest"
                                layout="double"
                                cardVariant="compact"
                                autoLoadOnScroll={false}
                                showLoadMoreButton={true}
                                maxItems={8}
                                pageSize={12}
                                // badge={{ text: category.name, color: "bg-blue-500" }}
                                className="mx-2 lg:mx-0"
                            />
                        ))}
                    </div>
                    <div className="pt-16 w-full flex flex-col order-first lg:order-none gap-4 lg:flex-1/4 py-8">
                        {/* Popular Post - Using DynamicNewsSection */}
                        <div className="bg-white dark:bg-gray-800/40 md:rounded-lg  overflow-hidden">
                            <h3 className="mx-4 my-2 text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <span className="text-[#de0b66]">🔥</span>
                                Berita Populer
                            </h3>
                            <div className="pl-6 md:pl-4 pr-2 lg:pr-4 pb-4">
                                <DynamicNewsSection
                                    type="popular"
                                    locationSlug={
                                        locationFilter.currentLocationSlug
                                    }
                                    sortBy="popular"
                                    variant="list"
                                    autoLoadOnScroll={false}
                                    showLoadMoreButton={false}
                                    showViewAll={false}
                                    maxItems={10}
                                    pageSize={15}
                                    className=""
                                />
                            </div>
                        </div>

                        {/* Tag Populer (Sidebar) */}
                        {tags?.length > 0 && (
                            <div className="bg-white dark:bg-gray-800/40 md:rounded-lg overflow-hidden">
                                <div className="p-4 ">
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Tag Populer
                                    </h3>
                                </div>
                                <div className="p-4">
                                    <div className="flex flex-wrap gap-2">
                                        {tags.slice(0, 15).map((tag: Tag) => (
                                            <SmartLink
                                                key={tag.id}
                                                href={`/tag/${tag.slug || tag.id}`}
                                                prefetchStrategy="hover"
                                                className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-full px-3 py-1 hover:bg-[#de0b66]/10 hover:text-[#de0b66] dark:hover:bg-[#de0b66]/20 dark:hover:text-pink-300 transition-colors"
                                            >
                                                {tag.name}
                                            </SmartLink>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Ads */}
                        {(() => {
                            const sidebarAds: AdItem[] = [
                                {
                                    id: "side-1",
                                    filename: "1080x1350_Lugu-Software.jpg",
                                    href: "https://software.lugu.id",
                                    orientation: "vertical",
                                    aspectRatio: "4/5",
                                    alt: "Vertical Software Ad",
                                },
                            ];
                            return (
                                <AdsBlock
                                    items={sidebarAds}
                                    className="md:rounded-lg mx-2 md:mx-0"
                                />
                            );
                        })()}
                    </div>
                </section>

                <section className="flex flex-col lg:flex-row items-start md:items-stretch gap-6 lg:gap-4">
                    <div className="flex flex-col lg:flex-3/4 gap-16 pt-16 lg:pr-8 lg:border-r border-r-gray-200 dark:border-gray-700">
                        <DynamicNewsSection
                            title="Berita Lainnya"
                            type="latest"
                            locationSlug={locationFilter.currentLocationSlug}
                            sortBy="latest"
                            layout="double"
                            cardVariant="compact"
                            useInfiniteScroll={true}
                            autoLoadOnScroll={true}
                            showLoadMoreButton={false}
                            showViewAll={false}
                            showEndIndicator={false}
                            maxItems={undefined}
                            pageSize={20}
                            className=" mx-2 lg:mx-0 mt-8"
                        />

                        {/* No fallback sections needed - DynamicNewsSection handles all data */}
                    </div>
                    <div className="pt-16 w-full flex flex-row lg:flex-col gap-4 lg:flex-1/4 py-8 ">
                        {/* flex > hidden*/}

                        {/* Ads */}
                        {(() => {
                            const sidebarAds: AdItem[] = [
                                {
                                    id: "side-1",
                                    filename: "1080x1350_Lugu-Software.jpg",
                                    href: "https://software.lugu.id",
                                    orientation: "vertical",
                                    aspectRatio: "4/5",
                                    alt: "Vertical Software Ad",
                                },
                            ];
                            return (
                                <AdsBlock
                                    items={sidebarAds}
                                    className="md:rounded-lg mx-2 md:mx-0"
                                />
                            );
                        })()}
                    </div>
                </section>
            </main>

            {/* Lapor Lambe floating button */}
            <LaporLambeButton />

            {/* Scroll to top button with fade in/out */}
            <ScrollToTop show={showScrollToTop} />
            <HomeFooter categories={categories} />
        </>
    );
};

export default Homepage;
